import React, { useEffect, useMemo, useState } from 'react';

import { faEquals, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonSizeVariants, ButtonVariants } from 'constants/common';
import { sortTablesByUpperCaseFirst } from 'constants/global';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import { JoinRow } from 'interfaces/report-templates.interface';
import { Button, Input, SelectFormInput } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';

type Props = {
  selectedColumnsData?: ReportingColumn[];
  control?: any;
  selectedTable: string | null;
  onCloseModal: VoidFunction;
  onSubmit: (joins: JoinRow[]) => void;
  initialJoins?: JoinRow[];
  selectedJoins?: JoinRow[];
};

const CreateReportTemplateJoinModal: React.FC<Props> = ({
  selectedColumnsData = [],
  control,
  selectedTable,
  onCloseModal,
  onSubmit,
  initialJoins,
  selectedJoins,
}) => {
  const { t } = useTranslation();

  const createNewJoin = (): JoinRow => ({
    id: Date.now(),
    leftTable: selectedTable ?? '',
    rightTable: '',
    leftColumn: '',
    rightColumn: '',
  });

  const [joins, setJoins] = useState<JoinRow[]>([]);
  const { setValue } = useFormContext();

  const handleJoinChange = (id: number, field: keyof JoinRow, value: string) => {
    setJoins((prev) => prev.map((join) => (join.id === id ? { ...join, [field]: value } : join)));
  };

  const addJoin = () => {
    setJoins((prev) => [...prev, createNewJoin()]);
  };

  const removeJoin = (id: number) => {
    setJoins((prev) => {
      const updated = prev.filter((join) => join.id !== id);
      return updated.length === 0 ? [createNewJoin()] : updated;
    });
  };

  const getMatchingColumns = (leftColumnName: string, leftTable: string, rightTable: string) => {
    if (!leftColumnName || !leftTable || !rightTable) {
      return [];
    }

    const leftColumn = selectedColumnsData.find((col) => col.tableName === leftTable && col.name === leftColumnName);

    if (!leftColumn?.columnType) {
      return [];
    }

    const rightTableColumns = selectedColumnsData.filter(
      (col) => col.tableName === rightTable && col.columnType === leftColumn.columnType
    );

    return rightTableColumns.map((col) => ({ label: col.name, value: col.name }));
  };

  const isAddDisabled = useMemo(() => {
    return !joins.every(
      (join) => join.leftTable?.trim() && join.rightTable?.trim() && join.leftColumn?.trim() && join.rightColumn?.trim()
    );
  }, [joins]);

  const rightTableOptions = useMemo(() => {
    const names = Array.from(
      new Set(selectedColumnsData.map((column) => column.tableName).filter((name) => name !== selectedTable))
    ).map((name) => ({ name }));

    const sorted = sortTablesByUpperCaseFirst(names);

    return sorted.map((table) => ({ label: table.name, value: table.name }));
  }, [selectedColumnsData, selectedTable]);

  const getCommonColumnsByType = (leftTable: string, rightTable: string) => {
    if (!leftTable || !rightTable) {
      return [];
    }

    const leftCols = selectedColumnsData.filter((col) => col.tableName === leftTable);
    const rightCols = selectedColumnsData.filter((col) => col.tableName === rightTable);

    const availableLeftColumns = leftCols
      .filter((leftColumn) => rightCols.some((rightColumn) => rightColumn.columnType === leftColumn.columnType))
      .map((col) => ({
        label: col.name,
        value: col.name,
      }));

    return availableLeftColumns;
  };

  const handleLeftColumnSelect = (id: number, table: string, columnName: string) => {
    const selectedColumn = selectedColumnsData.find((col) => col.tableName === table && col.name === columnName);

    setJoins((prev) =>
      prev.map((j) =>
        j.id === id
          ? {
              ...j,
              leftColumn: columnName,
              leftDisplayType: selectedColumn?.displayType || '',
              rightColumn: '',
            }
          : j
      )
    );
  };

  useEffect(() => {
    const source = selectedJoins ?? initialJoins;
    if (source?.length) {
      setJoins(source);
      source.forEach((join) => {
        setValue(`rightTable-${join.id}`, {
          label: join.rightTable,
          value: join.rightTable,
        });

        setValue(`leftColumn-${join.id}`, {
          label: join.leftColumn,
          value: join.leftColumn,
        });

        setValue(`rightColumn-${join.id}`, {
          label: join.rightColumn,
          value: join.rightColumn,
        });
      });
    } else {
      setJoins([createNewJoin()]);
    }
  }, [JSON.stringify(selectedJoins), selectedTable]);

  return (
    <div className="pt-4 mb-2">
      {joins.map((join) => (
        <div key={join.id} className="flex gap-1 items-center mt-3">
          <Input type="text" value={join.leftTable} inputClassName="!w-[10rem] !text-bodyMd-semibold" disabled />
          <FontAwesomeIcon icon={faPlus} className="text-neutral-900 dark:text-neutral-100" />
          <SelectFormInput
            inputClassName="!w-[10rem]"
            name={`rightTable-${join.id}`}
            control={control}
            options={rightTableOptions}
            onSelectValue={(option) => handleJoinChange(join.id, 'rightTable', option?.value ?? '')}
            placeholder={t('SelectTable')}
            isClearable
          />
          <span className="text-neutral-900 dark:text-neutral-100">On</span>
          <SelectFormInput
            inputClassName="!w-[10rem]"
            name={`leftColumn-${join.id}`}
            control={control}
            options={(() => {
              const options = getCommonColumnsByType(join.leftTable, join.rightTable);
              return options;
            })()}
            onSelectValue={(option) => {
              handleLeftColumnSelect(join.id, join.leftTable, option?.value || '');
            }}
            placeholder={t('SelectColumn')}
            disabled={!join.rightTable}
          />
          <FontAwesomeIcon icon={faEquals} className="text-neutral-900 dark:text-neutral-100" />
          <SelectFormInput
            inputClassName="!w-[10rem]"
            name={`rightColumn-${join.id}`}
            control={control}
            options={(() => {
              const options = getMatchingColumns(join.leftColumn, join.leftTable, join.rightTable);
              return options;
            })()}
            onSelectValue={(option) => {
              const value = option?.value || '';
              setJoins((prev) => prev.map((j) => (j.id === join.id ? { ...j, rightColumn: value } : j)));
            }}
            placeholder={t('SelectColumn')}
            disabled={!join.leftColumn}
          />

          <Button
            type="button"
            onClick={() => removeJoin(join.id)}
            variant={ButtonVariants.DESTRUCTIVE}
            size={ButtonSizeVariants.SM}
            className="w-6"
          >
            <FontAwesomeIcon icon={faTrashCan} className="text-white-00 w-4 h-4" />
          </Button>
        </div>
      ))}

      <Button
        className="mt-3"
        type="button"
        onClick={addJoin}
        variant={ButtonVariants.OUTLINE}
        size={ButtonSizeVariants.SM}
        disabled={isAddDisabled}
      >
        <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-1" />
        {t('AddJoin')}
      </Button>

      <ModalControlButtons
        isShownSubmit
        onClose={onCloseModal}
        saveButtonText={'Create'}
        disabled={isAddDisabled}
        onSubmit={() => onSubmit(joins)}
      />
    </div>
  );
};

export default CreateReportTemplateJoinModal;
